// use-bulk-task-update.js: // Page view for editing a single task in detail.
// @ts-check
import { useCallback } from 'react';
import { useProjectLimitGuard } from '@/features/wall';
import { checkAndAddTerms } from '@/features/tasks/utils/data/taskTags';
import { updateTaskSchema } from '@/features/tasks/utils/data/taskSchema';
import {
  getTaskBatch,
  commitBatch,
  updateTaskInBatch,
} from '@/services/taskMutations';

import { calculateNewRescheduleCount } from '@/features/tasks/utils/mutations/taskMutationUtils';
import {
  buildTasksMap,
  isAssigningSchedule,
  isSchedulingBlocked,
} from '@/features/tasks/utils/scheduling/taskSchedulingUtils';
const useBulkTaskUpdate = () => {
  const { guardProjects } = useProjectLimitGuard();

  /**
   * Applies the same set of field updates to multiple tasks in a single
   * Firestore batch write.
   *
   * @param {string[]} taskIds - IDs of the tasks to update. Must be non-empty.
   * @param {UpdateTask} updates - Partial task fields to apply to every task.
   * @param {string}   userId  - The authenticated user's UID.
   * @param {Task[]}   [allTasks] - Optional: full list of tasks to avoid blind reschedule increments.
   * @returns {Promise<void>} Resolves when the batch is committed.
   */
  const updateMultipleTasks = useCallback(
    async (taskIds, updates, userId, allTasks = []) => {
      if (!Array.isArray(taskIds) || taskIds.length === 0) {
        console.error('Invalid taskIds for bulk update');
        return;
      }
      if (!userId) {
        console.error('User ID is required for bulk update.');
        return;
      }

      const validationResult = updateTaskSchema.safeParse(updates);
      if (!validationResult.success) {
        console.error(
          'Validation failed for bulk task update:',
          validationResult.error.flatten(),
        );
        return;
      }

      const batch = getTaskBatch();
      const finalUpdates = { ...validationResult.data };

      // Sync taxonomy terms before touching Firestore task documents.
      // If this fails, the batch must NOT be committed — data would be inconsistent
      // (tasks would reference projects/contexts that don't exist in the user's
      // collections). Re-throw so the caller can show an error to the user.
      try {
        if (finalUpdates.projects !== undefined) {
          if (!guardProjects(finalUpdates.projects)) {
            return;
          }
          await checkAndAddTerms(finalUpdates.projects, 'projects', userId);
        }
        if (finalUpdates.contexts !== undefined) {
          await checkAndAddTerms(finalUpdates.contexts, 'contexts', userId);
        }
      } catch (error) {
        console.error(
          'Failed to sync taxonomy terms, aborting bulk update:',
          error,
        );
        throw error;
      }

      taskIds.forEach((taskId) => {
        const originalTask = allTasks.find((t) => t.id === taskId);
        const individualUpdates = { ...finalUpdates };

        if (
          originalTask &&
          isAssigningSchedule(individualUpdates) &&
          isSchedulingBlocked(originalTask, buildTasksMap(allTasks))
        ) {
          return;
        }

        if (originalTask) {
          const newRescheduleCount = calculateNewRescheduleCount(
            originalTask,
            individualUpdates,
          );
          if (newRescheduleCount !== (originalTask.rescheduleCount || 0)) {
            individualUpdates.rescheduleCount = newRescheduleCount;
          }
        }

        updateTaskInBatch(batch, taskId, individualUpdates);
      });

      try {
        await commitBatch(batch);
      } catch (error) {
        console.error('Firestore error during bulk update:', error);
        throw error;
      }
    },
    [guardProjects],
  );

  return {
    updateMultipleTasks,
  };
};

export default useBulkTaskUpdate;
